import {
  Cesium3DTileFeature,
  Cesium3DTileset,
  Entity,
  GroundPolylinePrimitive,
  GroundPrimitive,
  Model,
  PointPrimitive,
  Primitive,
} from "@cesium/engine";

import { DitapAlert, MeasurementVolume, Viewer } from "@cesium/widgets";
import DitapViewer from "../basic/DitapViewer";

/**
 * 객체 삭제를 위한 편집 기능 클래스.</br>
 * Editing feature class for object deletion.
 *
 * @alias EditRemove
 * @constructor
 *
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 *
 * @example
 * const viewer = new DitapViewer("ditapContainer");
 *
 * // 객체 생성 후 InteractionRemove를 통해 객체 삭제
 * const editRemove = new Ditap.EditRemove(viewer);
 *
 * const interactionRemove = new Ditap.InteractionRemove();
 * interactionRemove.editRemove = editRemove;
 */
class EditRemove {
  constructor(viewer) {
    if (viewer instanceof Viewer || viewer === undefined) {
      this._viewer = viewer;
    } else {
      console.error("Type doesn't match");
    }
    this._removeAlert = undefined;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 삭제 알림창.</br>
   * A waring alert.
   * @type {DitapAlert}
   */
  set removeAlert(removeAlert) {
    this._removeAlert = removeAlert;
  }
  get removeAlert() {
    return this._removeAlert;
  }

  /**
   * 인스턴스 파괴.</br>
   * Destroys the instance.
   */
  destroy() {
    if (this.viewer) {
      this.viewer = undefined;
    }
  }

  /*
   * 다이탭 엔티티 제거.</br>
   * Remove Ditap entity.
   * @param {Entity} removeEntity 제거할 엔티티.</br> The entity to be removed.
   * @param {DitapAlert} removeAlert 삭제 여부 묻는 알림창.<br> Notification window asking if you want to delete.
   */
  removeDitapEntity(removeEntity, removeAlert) {
    removeAlert.callback = () => {
      this.removeDitapPopup(removeEntity);
      removeEntity.destroy();
    };
    removeAlert.open();
  }

  /*
   * 다이탭 팝업 제거.</br>
   * Remove Ditap popup.
   * @param {Entity} removeEntity 제거할 엔티티.</br> The entity to be removed.
   */
  removeDitapPopup(removeEntity) {
    const removePopup = this.viewer.ditapStorage.getPopupInstanceById(
      removeEntity.id
    );
    removePopup?.destroy();

    this.viewer.ditapStorage.removePopupInstanceById(removeEntity.id);
  }

  /*
   * 아이디로 엔티티 제거.<br>
   * Remove entity by id.
   * @param {string} id 제거할 엔티티 아이디.</br> Entity id.
   * @param {DitapAlert} removeAlert 삭제 여부 묻는 알림창.<br> Notification window asking if you want to delete.
   */
  removeEntityById(id, removeAlert) {
    removeAlert.callback = () => {
      removeAlert.closeCallback();
      this.viewer.entities.removeById(id);
    };
    removeAlert.open();
  }

  /*
   * 컬렉션에서 프리미티브 제거.</br>
   * Removes a primitive from the collection.
   * @param {PrimitiveCollection} primitive 제거할 프리미티브.</br> Primitive to remove.
   * @param {DitapAlert} removeAlert 삭제 여부 묻는 알림창.<br> Notification window asking if you want to delete.
   */
  removePrimitive(primitive, removeAlert) {
    removeAlert.callback = () => {
      removeAlert.closeCallback();
      this.viewer.scene.primitives.remove(primitive);
    };

    removeAlert.open();
  }

  /*
   * 다양한 종류의 객체 제거.</br>
   * Execute remove functions based on the type of object.
   * @param {object} removeObject 제거할 객체.</br> Object to remove.
   * @param {DitapAlert} removeAlert 삭제 여부 묻는 알림창.<br> Notification window asking if you want to delete.
   */
  removeModelByType(removeObject, removeAlert) {
    if (this.viewer.ditapStorage.getDitapEntity(removeObject)) {
      const ditapEntity = this.viewer.ditapStorage.getDitapEntity(removeObject);

      if (
        ditapEntity instanceof MeasurementVolume &&
        ditapEntity.positions.length === 0
      ) {
        // 엔티티가 체적이면서 체적 계산 완료된 경우에만 삭제 가능
        // 경고창 생성
        const warningAelrt = new DitapAlert({
          type: "warning",
          title: "경고",
          content1: "체적 계산 중인 경우 삭제가 불가합니다.",
          buttons: "ONLYOK",
          open: false,
          callback: (alert) => {
            alert.destroy();
          },
          closeCallback: (alert) => {
            alert.destroy();
          },
        });
        warningAelrt.open();
        return;
      }
      this.removeDitapEntity(ditapEntity, removeAlert);
    } else if (
      removeObject.primitive instanceof Model ||
      removeObject.primitive instanceof Primitive ||
      removeObject.primitive instanceof PointPrimitive ||
      removeObject.primitive instanceof GroundPrimitive ||
      removeObject.primitive instanceof GroundPolylinePrimitive ||
      removeObject instanceof Cesium3DTileFeature
    ) {
      // 세슘 모델 중 primitive.id가 Entity인 경우가 있음
      if (removeObject.primitive.id instanceof Entity) {
        this.removeEntityById(removeObject.primitive.id.id, removeAlert);
      } else if (removeObject.id instanceof Entity) {
        this.removeEntityById(removeObject.id.id, removeAlert);
      } else {
        this.removePrimitive(removeObject.primitive, removeAlert);
      }
    } else if (removeObject instanceof Cesium3DTileset) {
      this.removePrimitive(removeObject, removeAlert);
    } else if (removeObject.primitive instanceof Cesium3DTileset) {
      this.removePrimitive(removeObject.primitive, removeAlert);
    }
  }
}
export default EditRemove;
